import { useMutation } from '@apollo/client';
import type {
  UpdateAssessmentActivityWithLinkedItemsMutation,
  UpdateAssessmentActivityWithLinkedItemsMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateAssessmentActivityWithLinkedItemsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateAssessmentActivityWithLinkedItemsTRPC } from './useUpdateAssessmentActivityWithLinkedItemsTRPC';

export const useUpdateAssessmentActivityWithLinkedItems = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation (legacy)
  const [updateAssessmentActivityGraphQL, graphqlState] = useMutation(
    UpdateAssessmentActivityWithLinkedItemsDocument,
    {
      update: (cache) => {
        evictField(cache, 'assessment_activity');
        evictField(cache, 'assessment');
        evictField(cache, 'compliance_monitoring_assessment');
        evictField(cache, 'internal_audit_report');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateAssessmentActivityWithLinkedItemsTRPC();

  const updateAssessmentActivity = async (
    variables: UpdateAssessmentActivityWithLinkedItemsMutationVariables
  ): Promise<UpdateAssessmentActivityWithLinkedItemsMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateAssessmentActivity({
        Id: variables.Id,
        ActivityType: variables.ActivityType,
        ParentId: variables.ParentId,
        Status: variables.Status,
        Summary: variables.Summary,
        Title: variables.Title,
        CompletionDate: variables.CompletionDate,
        AssignedUser: variables.AssignedUser,
        OriginalTimestamp: variables.OriginalTimestamp,
        LinkedItemIds:
          variables.LinkedItemIds == null
            ? []
            : Array.isArray(variables.LinkedItemIds)
              ? variables.LinkedItemIds
              : [variables.LinkedItemIds],
        CustomAttributeData: variables.CustomAttributeData,
        IsWizardAction: variables.IsWizardAction ?? undefined,
        OwnerUserIds:
          variables.OwnerUserIds == null
            ? []
            : Array.isArray(variables.OwnerUserIds)
              ? variables.OwnerUserIds
              : [variables.OwnerUserIds],
        OwnerGroupIds:
          variables.OwnerGroupIds == null
            ? []
            : Array.isArray(variables.OwnerGroupIds)
              ? variables.OwnerGroupIds
              : [variables.OwnerGroupIds],
      });
    }

    const result = await updateAssessmentActivityGraphQL({
      variables,
    });

    if (!result.data) {
      throw new Error('Failed to update assessment activity');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateAssessmentActivity,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateAssessmentActivity,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
